import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
import ReferralsAPI from 'api/ReferralsAPI'
import PracticesAPI from 'api/PracticesAPI'
import UsersAPI from 'api/UsersAPI'
import PatientAPI from 'api/PatientAPI'
import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
import createConfig from '../i18n/locales/en/pages/practice/create.js'
import { buildProposeTimeQuery, getProposeTimeUserId } from '@/utils/referralPoolProposeFlow'

export default {
    data() {
        return {
            referral: null,
            locations: [],
            selectedReferralConstraint: {},
            isReasonCodeModalOpen: false,
            isPracticeCreationOpen: false,
            showAvailableLocationsModal: false,
            isFlowInitializing: false,
            isFlowReady: false,
        }
    },

    computed: {
        reasonKey() {
            const reasonCode = this.selectedReferralConstraint?.reasonCode
            if (!reasonCode) return 'DEFAULT'
            if (typeof reasonCode === 'string') return reasonCode
            if (typeof reasonCode === 'object') return reasonCode.code || 'DEFAULT'
            return 'DEFAULT'
        },
        reasonConfig() {
            return createConfig.reason_codes[this.reasonKey] || createConfig.reason_codes.DEFAULT
        },
        reasonMessage() {
            return this.selectedReferralConstraint?.reasonMessage || this.reasonConfig.message
        },
    },

    methods: {
        buildProposeTimeQuery(referral, locationId, distance) {
            return buildProposeTimeQuery(this.$auth, referral, locationId, distance)
        },

        async loadScheduleContext() {
            const query = this.$route.query
            const isFromPatient = query.isFromPatient === true || query.isFromPatient === 'true' || query.isFromPatient === '1'
            const validPatientId =
                query.patientId && query.patientId !== 'null' && query.patientId !== 'undefined' ? Number(query.patientId) : null

            if (isFromPatient && validPatientId) {
                this.patient = await PatientAPI.getPatientDetails(validPatientId, this.$auth.getUser().id)
                return
            }

            const userId = query.userId || getProposeTimeUserId(this.$auth)
            if (query.practiceType === PracticeTypeEnum.PHYSICIAN || userId) {
                this.physician = await UsersAPI.get(userId)
                return
            }

            if (query.practiceId) {
                this.practice = await PracticesAPI.getPracticeById(query.practiceId)
            }
        },

        async finishProposeTimeFlow(referral, locationId, distance) {
            await this.applyProposeTimeRoute(referral, locationId, distance)
            await this.loadScheduleContext()
            this.isFlowReady = true
        },

        isReferralRoute() {
            return Boolean(
                this.$route.params.referralId ||
                    this.$route.name === 'referrals.propose.time' ||
                    (!this.$route.params.poolId && this.$route.path.startsWith('/referrals/')),
            )
        },

        referralId() {
            return this.$route.params.referralId || this.$route.params.poolId
        },

        hasReasonCode(referral) {
            const reasonCode = referral?.reasonCode
            if (reasonCode === null || reasonCode === undefined) return false
            if (typeof reasonCode === 'string') return reasonCode.trim().length > 0
            if (typeof reasonCode === 'object') return Object.keys(reasonCode).length > 0
            return true
        },

        async initProposeTimeFlow() {
            if (this.isFlowReady || this.isFlowInitializing) return

            this.isFlowInitializing = true
            try {
                const isReferral = this.isReferralRoute()
                const entityId = this.referralId()
                const referralType = this.$route.query.referralType || 'received'
                const activeUserId = this.$auth.isPracticeAncillary()
                    ? null
                    : this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                const practiceId = this.$auth.isPracticeAncillary() ? this.$auth.getPractice()?.id : null

                let referral
                if (isReferral) {
                    referral = await ReferralsAPI.get(entityId, activeUserId)
                } else {
                    referral = await ReferralsPoolAPI.getReferralPoolById(entityId, referralType, activeUserId, practiceId)
                    if (this.hasReasonCode(referral)) {
                        const result = await ReferralsPoolAPI.promoteConstrained(entityId)
                        if (result?.promoted === false) {
                            this.selectedReferralConstraint = result
                            this.isReasonCodeModalOpen = true
                            return
                        }
                    }

                    if (referral.insurancePlanId) {
                        await PracticesAPI.acceptInsuranceAndTests(
                            this.$auth.getPractice().id,
                            [referral.insurancePlanId],
                            referral.referralTests,
                        )
                    }
                }
                this.referral = referral

                if (this.$route.query.pendingInit !== '1') {
                    await this.finishProposeTimeFlow(referral, Number(this.$route.params.locationId) || 0)
                    return
                }

                const targetLocationId =
                    referral.toPracticeLocationId || referral.practiceLocationId || referral.practiceLocation?.id
                if (targetLocationId) {
                    await this.finishProposeTimeFlow(referral, Number(targetLocationId))
                    return
                }

                const locationQuery = {
                    address: referral.searchBy?.address || referral.patient?.address || undefined,
                    city: referral.searchBy?.city || referral.patient?.city || undefined,
                    state: referral.searchBy?.state || referral.patient?.state || undefined,
                    zip: referral.searchBy?.zip || referral.patient?.zip || undefined,
                    patientId: referral.patientId || referral.patient?.id,
                    referralPoolId: !isReferral ? referral.id : undefined,
                }
                const activeMemberId = this.$auth.getActiveMember()?.id
                this.locations = await (this.$auth.isPracticeAncillary()
                    ? ReferralsPoolAPI.getAncillaryAvailableLocations(this.$auth.getPractice().id, locationQuery)
                    : ReferralsPoolAPI.getAvailableLocations(activeMemberId, locationQuery))

                if (!this.locations.length) {
                    await this.finishProposeTimeFlow(referral, Number(this.$route.params.locationId) || 0)
                    return
                }

                if (this.locations.length > 1) {
                    this.showAvailableLocationsModal = true
                    return
                }

                const locationId = this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY ? 0 : this.locations[0].id
                await this.finishProposeTimeFlow(referral, locationId, this.locations[0]?.distanceToPatient)
            } catch (e) {
                this.$router.back()
            } finally {
                this.isFlowInitializing = false
            }
        },

        applyProposeTimeRoute(referral, locationId, distance) {
            const isReferral = this.isReferralRoute()
            const routeName = isReferral ? 'referrals.propose.time' : 'referrals.pool.propose.time'
            const params = isReferral ? { referralId: referral.id, locationId } : { poolId: referral.id, locationId }

            return this.$router.replace({
                name: routeName,
                params,
                query: {
                    referralType: this.$route.query.referralType || 'received',
                    ...this.buildProposeTimeQuery(referral, locationId, distance),
                },
            })
        },

        async onProposeLocationSelected(event) {
            this.showAvailableLocationsModal = false
            const locationId = event.practiceLocationId ?? event.id
            await this.finishProposeTimeFlow(this.referral, locationId, event.distance)
        },

        onProposeLocationsModalClose() {
            this.showAvailableLocationsModal = false
            if (!this.isFlowReady) {
                this.$router.back()
            }
        },

        closeReasonCodeModal() {
            this.selectedReferralConstraint = {}
            this.isReasonCodeModalOpen = false
            this.$router.back()
        },

        runReasonAction() {
            if (!this.reasonConfig.action) return
            if (this.reasonConfig.action === 'GO_TO_PROFILE') {
                this.$router.push({ name: 'practice.profile' })
                return
            }
            if (this.reasonConfig.action === 'GO_TO_BILLING') {
                this.$router.push({
                    name: 'practice.billing',
                    query: { redirect: this.$route.fullPath },
                })
                return
            }
            if (this.reasonConfig.action === 'GO_TO_PRACTICE_CREATE') {
                this.togglePracticeCreationModal()
            }
        },

        togglePracticeCreationModal() {
            this.isPracticeCreationOpen = !this.isPracticeCreationOpen
        },

        goToCreatePractice() {
            this.isPracticeCreationOpen = false
            this.$router.push({
                name: 'company.create',
                query: { redirect: this.$route.fullPath },
            })
        },
    },
}
